import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Alert,
    CircularProgress,
    Button,
    Grid,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';

import axios from 'axios';
import { API_PATH } from "../../../App";
import { getAxiosConfig } from "../../../helpers/AuthHelper";

interface MismatchDetails {
    workerId: string;
    machineModel: string;
    dbHashrate: string;
    poolHashrate: string;
}

interface MissingDetails {
    workerId: string;
    extraInfo: string;
}

interface AuditReport {
    mismatchedMachines: MismatchDetails[];
    activeInPoolOffInDb: MissingDetails[];
    activeInDbOffInPool: MissingDetails[];
}

const SegpoolAudit = () => {
    const [report, setReport] = useState<AuditReport | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_PATH}/audit/segpool`, getAxiosConfig());
            setReport(response.data);
        } catch (err: any) {
            console.error(err);
            setError('Failed to fetch audit report. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    const mismatchCount = report?.mismatchedMachines.length || 0;
    const poolOnlyCount = report?.activeInPoolOffInDb.length || 0;
    const cloudOnlyCount = report?.activeInDbOffInPool.length || 0;

    const noIssues = mismatchCount === 0 && poolOnlyCount === 0 && cloudOnlyCount === 0;

    return (
        <Box p={2}>
            {/* Header with Refresh */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight="bold">Segpool Audit Dashboard</Typography>
                <Button
                    variant="contained"
                    startIcon={<ReplayIcon />}
                    onClick={fetchReport}
                    color="primary"
                >
                    Refresh
                </Button>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} mb={4}>
                {noIssues && (
                    <Grid item xs={12}>
                        <Alert severity="success" icon={<CheckCircleOutlineIcon fontSize="inherit" />}>
                            All Systems Operational - No Discrepancies Found
                        </Alert>
                    </Grid>
                )}

                <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: mismatchCount > 0 ? '#fff3e0' : 'background.paper', borderLeft: '6px solid #ff9800' }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" variant="overline" fontWeight="bold">Hashrate Mismatch</Typography>
                                    <Typography variant="h3" color="warning.main">{mismatchCount}</Typography>
                                </Box>
                                <WarningIcon sx={{ fontSize: 60, color: '#ff9800', opacity: 0.5 }} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: poolOnlyCount > 0 ? '#e3f2fd' : 'background.paper', borderLeft: '6px solid #2196f3' }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" variant="overline" fontWeight="bold">Active In Pool / Off Cloud</Typography>
                                    <Typography variant="h3" color="info.main">{poolOnlyCount}</Typography>
                                </Box>
                                <CloudQueueIcon sx={{ fontSize: 60, color: '#2196f3', opacity: 0.5 }} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: cloudOnlyCount > 0 ? '#ffebee' : 'background.paper', borderLeft: '6px solid #f44336' }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography color="textSecondary" variant="overline" fontWeight="bold">Active In Cloud / Off Pool</Typography>
                                    <Typography variant="h3" color="error.main">{cloudOnlyCount}</Typography>
                                </Box>
                                <CloudOffIcon sx={{ fontSize: 60, color: '#f44336', opacity: 0.5 }} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Detailed Sections (Accordions) */}

            {/* Mismatch Section */}
            <Accordion defaultExpanded={mismatchCount > 0} disableGutters elevation={2} sx={{ mb: 2, borderRadius: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center">
                        <WarningIcon color="warning" sx={{ mr: 2 }} />
                        <Typography variant="h6" color="textPrimary">
                            Hashrate Mismatches ({mismatchCount})
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'grey.100' }}>
                                <TableRow>
                                    <TableCell>Worker ID</TableCell>
                                    <TableCell>Model</TableCell>
                                    <TableCell>Cloud Hashrate</TableCell>
                                    <TableCell>Pool Hashrate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report?.mismatchedMachines.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 3, fontStyle: 'italic', color: 'text.secondary' }}>
                                            No hashrate mismatches found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    report?.mismatchedMachines.map((row) => (
                                        <TableRow hover key={row.workerId}>
                                            <TableCell sx={{ fontWeight: 500 }}>{row.workerId}</TableCell>
                                            <TableCell>{row.machineModel}</TableCell>
                                            <TableCell>{row.dbHashrate}</TableCell>
                                            <TableCell sx={{ color: 'error.main', fontWeight: 'bold' }}>{row.poolHashrate}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>

            {/* In Pool / Off Cloud */}
            <Accordion defaultExpanded={poolOnlyCount > 0} disableGutters elevation={2} sx={{ mb: 2, borderRadius: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center">
                        <CloudQueueIcon color="info" sx={{ mr: 2 }} />
                        <Typography variant="h6" color="textPrimary">
                            Active in Pool / OFF in Cloud ({poolOnlyCount})
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'grey.100' }}>
                                <TableRow>
                                    <TableCell>Worker ID</TableCell>
                                    <TableCell>Details</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report?.activeInPoolOffInDb.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center" sx={{ py: 3, fontStyle: 'italic', color: 'text.secondary' }}>
                                            No discrepancies found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    report?.activeInPoolOffInDb.map((row) => (
                                        <TableRow hover key={row.workerId}>
                                            <TableCell sx={{ fontWeight: 500 }}>{row.workerId}</TableCell>
                                            <TableCell>{row.extraInfo}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>

            {/* In Cloud / Off Pool */}
            <Accordion defaultExpanded={cloudOnlyCount > 0} disableGutters elevation={2} sx={{ mb: 2, borderRadius: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center">
                        <CloudOffIcon color="error" sx={{ mr: 2 }} />
                        <Typography variant="h6" color="textPrimary">
                            Active in Cloud / OFF in Pool ({cloudOnlyCount})
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'grey.100' }}>
                                <TableRow>
                                    <TableCell>Worker ID</TableCell>
                                    <TableCell>Details</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report?.activeInDbOffInPool.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center" sx={{ py: 3, fontStyle: 'italic', color: 'text.secondary' }}>
                                            No discrepancies found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    report?.activeInDbOffInPool.map((row) => (
                                        <TableRow hover key={row.workerId}>
                                            <TableCell sx={{ fontWeight: 500 }}>{row.workerId}</TableCell>
                                            <TableCell>{row.extraInfo}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default SegpoolAudit;
